/**
 * Auth flow: bootstrap, login, logout, token retrieval.
 *
 * This module is the single owner of:
 *   - the in-memory access-token cache (via tokenMemory)
 *   - the page-load /me hydration (validates token + refreshes permissions)
 *   - logout-side cleanup (storage + query cache + cross-tab broadcast)
 *
 * Components NEVER call `tokenMemory.set()` directly — they call `login()`
 * or rely on `bootstrapAuth()` at app start.
 *
 * Current backend reality (Phase B activation):
 *   ✅ POST /v1/auth/login    — returns authenticationToken + PII fields
 *   ✅ GET  /me               — returns user/roles/permissions
 *   ❌ POST /v1/auth/refresh  — NOT IMPLEMENTED on backend yet.
 *                              Token persistence on reload uses the
 *                              existing JS-readable cookie. When backend
 *                              ships /v1/auth/refresh, uncomment the
 *                              refresh logic below and remove cookie-based
 *                              fallbacks (token becomes in-memory only).
 *   ❌ POST /v1/auth/logout   — NOT IMPLEMENTED on backend yet.
 *                              Logout proceeds with client-side cleanup
 *                              only. When backend ships, uncomment the
 *                              best-effort POST in logout() below.
 *
 * Reload behavior (F5 / hard refresh):
 *   1. tokenMemory is empty (JS state lost).
 *   2. bootstrapAuth() reads the token cookie → mirrors into tokenMemory.
 *   3. bootstrapAuth() calls GET /me with the token. The /me round-trip
 *      doubles as (a) token-validity check (401 ⇒ force re-login) and
 *      (b) permission refresh — if the admin revoked a permission while
 *      the user was logged in, the next reload picks up the new state.
 *
 * See:
 *   - docs/adr/0009-storage-architecture-and-token-strategy.md (Phase status)
 *   - docs/api-contracts/auth.md
 */

import { sharedAxios } from './axios'
import { queryClient } from './queryClient'
import { tokenMemory, broadcastLogin, broadcastLogout, clearAllUserScoped } from '@/shared/storage'
import { useAuthStore } from '@/entities/auth/model'
import { getCookie, setCookie, deleteAllCookies } from '@/shared/lib/formatter'

// ---------- API response shapes ----------

/**
 * Current backend's /v1/auth/login response payload.
 * Migrates to LoginPayload (with `accessToken`, `expiresIn`, nested `user`)
 * when backend updates — see docs/api-contracts/auth.md.
 */
interface CurrentLoginPayload {
  authenticationToken: string
  expiresAt: string
  refreshToken?: string
  fullName?: string
  userId: number | string
  username?: string
  // Some backend responses include a nested user object too; read both
  // shapes if present so we're forward-compatible with the new contract.
  user?: AuthUserPayload
}

interface AuthUserPayload {
  id: number | string
  email?: string | null
  fullname?: string
  /** Tolerate either casing — login response uses camelCase, /me uses lowercase. */
  fullName?: string
  /** Single-role string returned by current `/me` backend (e.g. "SUPER_ADMIN"). */
  role?: string
  /** Forward-compat slot for a future `roles` array shape. */
  roles?: unknown
  /** /me's `photo` object — `path` is the full avatar URL. */
  photo?: {
    id?: number
    name?: string
    path?: string
    [key: string]: unknown
  }
  /** Resource-keyed permission map (canonical for runtime checks). */
  permissions2?: unknown
  /** Human-labeled permission tree (Russian/Uzbek keys) — informational. */
  permissions?: unknown
  /** Not present in current /me; kept optional for forward-compat. */
  nickname?: string
}

interface ApiEnvelope<T> {
  success: boolean
  data?: T
  message?: string
  meta?: T
}

// ---------- Internal helpers ----------

/**
 * Convert an `expiresAt` ISO/timestamp string into seconds-from-now.
 * Floors at 0 — never returns negative.
 */
function expiresAtToSeconds(expiresAt: string): number {
  const ms = new Date(expiresAt).getTime() - Date.now()
  return Math.max(0, Math.floor(ms / 1000))
}

/**
 * Apply a /me-shaped user payload into the Zustand auth store. Casts at
 * the boundary because the existing AuthUser/RoleItem/PermissionMap
 * types are loose (legacy from the migration).
 *
 * Real /me response shape (current backend):
 *   { id, email, fullname, photo: { path }, role: "<NAME>",
 *     permissions2: { ... }, permissions: { ... } }
 *
 * Note: /me does NOT return a token. The token is set by `login()` into
 * the cookie and is restored on reload by `getAccessToken()` reading
 * the cookie. /me's job is (a) validate the token (401 ⇒ logout via
 * axios interceptor), (b) hydrate user data, (c) refresh permissions.
 */
function applyUserToStore(user: AuthUserPayload): void {
  const auth = useAuthStore.getState()
  auth.setUser({
    id: user.id,
    email: user.email ?? undefined,
    fullname: user.fullname ?? user.fullName,
    nickname: user.nickname,
    userId: typeof user.id === 'number' ? user.id : Number(user.id),
    imageUrl: user.photo?.path,
  } as never)

  // Backend returns a single-role string today. RoleItem is typed as
  // `string | { id, name, description }`, so wrapping the string into
  // a single-element array is type-compatible with setUserRoles.
  // If `roles` (array) ever lands in a future response shape, prefer it.
  if (Array.isArray(user.roles)) {
    auth.setUserRoles(user.roles as never)
  } else if (typeof user.role === 'string') {
    auth.setUserRoles([user.role] as never)
  }

  if (user.permissions2 !== undefined) {
    auth.setPermissions(user.permissions2 as never)
  }
}

/**
 * Fetch /me, hydrate the Zustand auth store. Returns the user payload
 * on success, null on any failure (network, 401, malformed response).
 *
 * The endpoint path is currently `/me` (no version prefix). When backend
 * versions to `/v1/me` for consistency with /v1/auth/*, update here.
 */
async function fetchMeAndHydrate(): Promise<AuthUserPayload | null> {
  try {
    const response = await sharedAxios.get<ApiEnvelope<AuthUserPayload>>('/me')
    const user = response.data.meta ?? response.data.data
    if (!response.data.success || !user) return null
    applyUserToStore(user)
    return user
  } catch {
    return null
  }
}

// ---------- Public API ----------

/**
 * Returns a valid access token, or null if no token is available.
 *
 * Phase B (current): reads tokenMemory, falls back to the existing token
 * cookie. tokenMemory is hydrated from the cookie on first call (sync),
 * and on login (sync). No async refresh path until backend ships
 * /v1/auth/refresh.
 *
 * Phase B+ (when backend ships): the body below is replaced by an async
 * refresh-with-deduplication flow — the commented code at the bottom of
 * this function is the future implementation.
 */
export function getAccessToken(): string | null {
  const cached = tokenMemory.get()
  if (cached) return cached

  // Cookie fallback: hydrate tokenMemory from the existing cookie so the
  // request interceptor and other callers see a consistent state.
  const fromCookie = getCookie('token')
  const expiresAt = getCookie('expiresAt')
  if (fromCookie && expiresAt) {
    const expiresInSec = expiresAtToSeconds(expiresAt)
    if (expiresInSec > 0) {
      tokenMemory.set(fromCookie, expiresInSec)
      return fromCookie
    }
  }

  return null

  // === PHASE B+ (uncomment when backend ships POST /v1/auth/refresh) ===
  // The flow becomes async with refresh deduplication:
  //
  //   refreshPromise ??= refreshAccessToken().finally(() => {
  //     refreshPromise = null
  //   })
  //   return refreshPromise
  //
  // and the cookie fallback above is removed (token becomes in-memory only,
  // restored on reload by /v1/auth/refresh against the httpOnly refresh
  // cookie). See docs/adr/0009-* and docs/api-contracts/auth.md.
}

// === PHASE B+ (uncomment when backend ships POST /v1/auth/refresh) ===
// let refreshPromise: Promise<string | null> | null = null
//
// async function refreshAccessToken(): Promise<string | null> {
//   try {
//     const response = await sharedAxios.post<ApiEnvelope<{ accessToken: string; expiresIn: number }>>(
//       '/v1/auth/refresh',
//       null,
//       { _skipAuthHeader: true },
//     )
//     if (!response.data.success || !response.data.data) {
//       tokenMemory.clear()
//       return null
//     }
//     const { accessToken, expiresIn } = response.data.data
//     tokenMemory.set(accessToken, expiresIn)
//     return accessToken
//   } catch {
//     tokenMemory.clear()
//     return null
//   }
// }

/**
 * Cold-start sequence on page load. Runs once via <AuthBootstrap>.
 *
 * Phase B (current):
 *   1. Read the existing token cookie (or empty → unauthenticated).
 *      Empty case ⇒ defensive cleanup of any stale storage / cookies /
 *      Zustand state and return false. Routing redirects to /login.
 *   2. GET /me with that token → 401 ⇒ axios response interceptor
 *      performs logout + redirect; this function then returns false.
 *   3. /me success ⇒ apply user/roles/permissions to Zustand, return true.
 *
 * The /me round-trip is intentional even when the token cookie is "fresh"
 * by expiry timestamp: it re-validates the token server-side AND refreshes
 * permissions (admin may have revoked a permission since login).
 *
 * Why the "no token" branch calls `logout()` rather than just returning
 * false: the token cookie may have been deleted externally (DevTools,
 * a browser extension, a parallel logout in another origin process)
 * while Zustand state, sessionStorage drafts, or the TanStack Query
 * cache still hold data from a prior session. Calling `logout()` makes
 * the client state consistent with the empty-token reality before
 * routing redirects. Idempotent on a fresh visit (sequence of no-ops
 * when there is nothing to clear).
 */
export async function bootstrapAuth(): Promise<boolean> {
  const token = getAccessToken()
  if (!token) {
    await logout()
    return false
  }

  const user = await fetchMeAndHydrate()
  return user !== null
}

/**
 * Authenticate with phone + password.
 *
 * Phase B (current backend response shape):
 *   - Reads `authenticationToken`, `expiresAt`, `userId` from response.
 *   - Sets the token cookie (so reload can restore via getAccessToken).
 *   - Mirrors the token into tokenMemory.
 *   - Calls /me internally to get full user/roles/permissions, hydrates
 *     Zustand. PII (fullName, userId, username, user) does NOT go into
 *     cookies — it lives in Zustand only.
 *   - Broadcasts to other tabs.
 *
 * Phase B+ (future, when backend updates response shape): drop the
 * authenticationToken→accessToken alias, drop the cookie writes (refresh
 * cookie replaces persistence), and read the `user` object directly from
 * the login response instead of a separate /me call.
 */
export async function login(phone: string, password: string): Promise<AuthUserPayload> {
  const response = await sharedAxios.post<ApiEnvelope<CurrentLoginPayload>>(
    '/v1/auth/login',
    { phone, password },
    { _skipAuthHeader: true },
  )

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message ?? 'Login failed')
  }

  const { authenticationToken, expiresAt, refreshToken, fullName, userId, username } =
    response.data.data
  const expiresInSec = expiresAtToSeconds(expiresAt)

  // Reject obviously-broken token lifetimes BEFORE writing any cookies.
  // tokenMemory enforces a 30s safety margin (memory.ts:33), so any token
  // shorter than ~60s is born effectively expired — `tokenMemory.get()`
  // returns null on the first post-login request, the request fires with
  // no Authorization header, the backend 401s, and the user lands back
  // in the session-expired loop. Common upstream causes: backend clock
  // skew, mis-shaped expiresAt string parsed as NaN, debug-build token
  // TTL set absurdly low. Surface explicitly instead of silently looping.
  if (expiresInSec < 60) {
    throw new Error(
      `Backend issued token with too-short lifetime (${expiresInSec}s). ` +
        `Tokens shorter than 60s cannot be used because tokenMemory enforces ` +
        `a 30s safety margin. Verify backend clock and expiresAt format.`,
    )
  }

  // Auth + PII cookies. The token + expiresAt cookies are required for
  // reload restoration (getAccessToken reads them). The PII cookies
  // (fullName / userId / username) are consumed by other parts of the
  // codebase that read them directly; useUserMe also overwrites
  // `username` with the /me response's fullname, that's expected.
  //
  // refreshToken: backend currently includes this in the login response
  // but the frontend has no /v1/auth/refresh endpoint to use it against.
  // Stored in cookie for forward-compatibility — when /v1/auth/refresh
  // ships in PR-2, it will be moved to an httpOnly cookie set by the
  // backend and removed from the JS-readable surface here.
  setCookie('token', authenticationToken, expiresAt)
  setCookie('expiresAt', expiresAt, expiresAt)
  if (refreshToken) setCookie('refreshToken', refreshToken, expiresAt)
  if (fullName) setCookie('fullName', fullName, expiresAt)
  setCookie('userId', String(userId), expiresAt)
  if (username) setCookie('username', username, expiresAt)

  // Mirror into in-memory cache so the request interceptor sees a
  // consistent token reference for the rest of the session.
  tokenMemory.set(authenticationToken, expiresInSec)

  // Hydrate full user / roles / permissions from /me BEFORE setting the
  // Zustand token. Order matters: setToken() triggers AuthManager's
  // tokenChanged effect which would call /me a second time. By hydrating
  // first, AuthManager can detect the user is already in the store and
  // skip the redundant /me call — eliminating the concurrent-request race.
  const user = await fetchMeAndHydrate()
  if (!user) {
    throw new Error('Failed to fetch user profile after login')
  }

  // Set Zustand token AFTER /me completes so AuthManager fires when user
  // data is already present in the store.
  useAuthStore.getState().setToken(authenticationToken)

  broadcastLogin(userId)
  return user
}

/**
 * Logout with full client-state cleanup.
 *
 * Phase B (current):
 *   1. Server-side logout API is not yet implemented — the POST below
 *      is commented out. Cookies + Zustand + caches all cleared
 *      client-side.
 *   2. Cookies (token + any PII left over) cleared via deleteAllCookies.
 *   3. clearAllUserScoped — in-memory token + drafts + sessionStorage.
 *   4. queryClient.clear — TanStack Query cache.
 *   5. broadcastLogout — other tabs in this origin sync logout.
 *   6. Zustand auth store reset.
 *
 * Steps 2-6 ALWAYS run, even if step 1 is later restored and throws.
 */
export async function logout(): Promise<void> {
  // === PHASE B+ (uncomment when backend ships POST /v1/auth/logout) ===
  // Best-effort server-side invalidation. Even if the call fails (or the
  // endpoint is missing), client-side cleanup proceeds.
  //
  // try {
  //   await sharedAxios.post('/v1/auth/logout', null)
  // } catch {
  //   // Server may be unreachable, or endpoint not yet deployed —
  //   // not a reason to leave the client in an authenticated state.
  // }

  deleteAllCookies()
  clearAllUserScoped()
  queryClient.clear()
  broadcastLogout()
  useAuthStore.getState().logout()
}
